import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './useReduxAuth';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_LEAVE_REQUESTS_QUERY } from '../graphql/queries/leaveRequests';
import {
  APPROVE_LEAVE_REQUEST_MUTATION,
  REJECT_LEAVE_REQUEST_MUTATION,
} from '../graphql/mutations/leaveRequests';
import { mergeWhereConditions } from '../utils/buildHasuraWhere';

export const useLeaveRequestsAdmin = () => {
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  // DataTable-driven Hasura `_bool_exp` for filter/search. Merged with the
  // role-derived `where` below so backend filtering covers all pages.
  const [extraWhereCondition, setExtraWhereConditionState] = useState(null);
  const extraWhereDepsKey = useMemo(
    () => (extraWhereCondition ? JSON.stringify(extraWhereCondition) : ''),
    [extraWhereCondition]
  );
  const setExtraWhereCondition = useCallback(next => {
    setExtraWhereConditionState(prev => {
      const prevKey = prev ? JSON.stringify(prev) : '';
      const nextKey = next ? JSON.stringify(next) : '';
      if (prevKey === nextKey) return prev;
      return next;
    });
  }, []);
  console.log('useLeaveRequestsAdmin - user:', user);
  const where = useMemo(() => {
    if (user?.role === 'ROOT') {
      return {};
    } else if (
      user?.role === 'DIRECTOR' ||
      user?.isLoanRequestManager === true
    ) {
      return { company_id: { _eq: user.companyId } };
    } else if (user?.role === 'GROUP_MANAGER') {
      const groupId = user.team?.group_id || user.group_id;
      console.log('useLeaveRequestsAdmin - GROUP_MANAGER groupId:', groupId);

      if (groupId) {
        return {
          company_id: { _eq: user.companyId },
          group_id: { _eq: groupId },
        };
      } else {
        return { company_id: { _eq: user.companyId } };
      }
    } else if (user?.role === 'TEAM_MANAGER') {
      return {
        company_id: { _eq: user.companyId },
        team_id: { _eq: user.team_id },
      };
    } else {
      return { company_id: { _eq: user.companyId } };
    }
  }, [
    user?.role,
    user?.companyId,
    user?.team_id,
    user?.team?.group_id,
    user?.group_id,
  ]);

  // Reset to page 1 whenever the where condition (filters / role scope or
  // user-driven DataTable filters) changes.
  useEffect(() => {
    setPageState(1);
  }, [where, extraWhereDepsKey]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const mergedWhere = mergeWhereConditions(where, extraWhereCondition);
      const data = await hasuraClient.query(GET_LEAVE_REQUESTS_QUERY, {
        where: mergedWhere,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      const count = data?.leave_requests_aggregate?.aggregate?.count ?? 0;
      setTotalCount(count);
      setHasNextPage(page * pageSize < count);

      const mappedData = (data.leave_requests || []).map(lr => ({
        id: lr.id,
        startDate: lr.start_date,
        endDate: lr.end_date,
        leaveTypeName: lr.leaveType?.leave_type_name || lr.leave_type,
        description: lr.leave_request_description || '',
        duration: lr.duration,
        status: lr.status,
        userId: lr.user_id,
        companyId: lr.company_id,
        teamId: lr.team_id,
        groupId: lr.group_id,
        is_team_manager_approved: lr.is_team_manager_approved,
        is_group_manager_approved: lr.is_group_manager_approved,
        approvingManager: lr.approving_manager
          ? {
              first_name: lr.approving_manager.first_name,
              last_name: lr.approving_manager.last_name,
            }
          : null,
        approvingManagerId: lr.approving_manager_id,
        user: lr.user,
        team: lr.user?.team,
        teamName: lr.user?.team?.name || '',
        team_name: lr.user?.team?.name || '',
        group: lr.user?.team?.group,
        groupName: lr.user?.team?.group?.name || '',
        group_name: lr.user?.team?.group?.name || '',
        companyName: lr.user?.company?.name || '',
        company_name: lr.user?.company?.name || '',
        // Kullanıcının kalan yıllık izin hakkı (saat cinsinden)
        remainingAnnualLeaveHours:
          (lr.user?.user_annual_leave_balance ?? 0) -
          (lr.user?.total_leaves_used ?? 0),
      }));

      setLeaveRequests(mappedData);
    } catch (e) {
      setLeaveRequests([]);
    } finally {
      setLoading(false);
    }
  }, [where, extraWhereCondition, page, pageSize]);

  const setPage = useCallback(p => {
    setPageState(p);
  }, []);

  const setPageSize = useCallback(size => {
    setPageSizeState(size);
    setPageState(1);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async leaveId => {
    let updateFields = {};
    const approvingManagerId = user?.id;
    if (user?.isLoanRequestManager === true) {
      updateFields = {
        is_final_approved: true,
        status: 'APPROVED',
        approving_manager_id: approvingManagerId,
      };
    } else if (user?.role === 'TEAM_MANAGER') {
      updateFields = {
        is_team_manager_approved: true,
        approving_manager_id: approvingManagerId,
      };
    } else if (user?.role === 'GROUP_MANAGER') {
      updateFields = {
        is_group_manager_approved: true,
        approving_manager_id: approvingManagerId,
      };
    } else if (user?.role === 'DIRECTOR' || user?.role === 'ROOT') {
      // For final approval, always set status to APPROVED
      updateFields = {
        is_final_approved: true,
        status: 'APPROVED',
        approving_manager_id: approvingManagerId,
      };
    }

    await hasuraClient.mutate(APPROVE_LEAVE_REQUEST_MUTATION, {
      id: leaveId,
      updateFields,
    });
    fetchData();
  };

  const handleReject = async (leaveId, reason) => {
    let updateFields = {};
    const approvingManagerId = user?.id;
    if (user?.isLoanRequestManager === true) {
      updateFields = {
        is_final_approved: false,
        status: 'REJECTED',
        approving_manager_id: approvingManagerId,
        reject_reason: reason,
      };
    } else if (user?.role === 'TEAM_MANAGER') {
      updateFields = {
        is_team_manager_approved: false,
        approving_manager_id: approvingManagerId,
        reject_reason: reason,
      };
    } else if (user?.role === 'GROUP_MANAGER') {
      updateFields = {
        is_group_manager_approved: false,
        approving_manager_id: approvingManagerId,
        reject_reason: reason,
      };
    } else if (user?.role === 'DIRECTOR' || user?.role === 'ROOT') {
      // For final rejection, always set status to REJECTED
      updateFields = {
        is_final_approved: false,
        status: 'REJECTED',
        approving_manager_id: approvingManagerId,
        reject_reason: reason,
      };
    }

    await hasuraClient.mutate(REJECT_LEAVE_REQUEST_MUTATION, {
      id: leaveId,
      updateFields,
    });
    fetchData();
  };

  return {
    leaveRequests,
    loading,
    handleApprove,
    handleReject,
    fetchData,
    serverPagination: true,
    page,
    pageSize,
    totalCount,
    hasNextPage,
    setPage,
    setPageSize,
    extraWhereCondition,
    setExtraWhereCondition,
  };
};
